import Checkbox from "@mui/material/Checkbox";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { EnrichedClub } from "@/module/room-configs-module/manager-config-module/utils";
import { getDate } from "@/lib/common-utils";

export const useManagerConfigTableColumns = () => {
  const { t } = useTranslation();

  return useMemo<MRT_ColumnDef<EnrichedClub>[]>(
    () => [
      {
        header: t("cmsAggregatorTable.enable"),
        filterFn: "contains",
        enableEditing: false,
        accessorFn: (row) => row.enabled ?? "-",
        size: 80,
        Cell: ({ row }) => <Checkbox checked={row.original.enabled} disabled />,
      },
      {
        header: t("clubEnabled"),
        filterFn: "contains",
        enableEditing: false,
        //accessorFn: (row) => row.enabled ?? "-",
        size: 80,
        Cell: ({ row }) => <Checkbox checked={row.original.clubEnabled} disabled />,
      },
      {
        header: t("cmsAggregatorTable.name"),
        accessorFn: (row) => row.name ?? "-",
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.username"),
        accessorFn: (row) => row.username ?? "-",
        filterFn: "contains",
      },


      //Club
      {
        header: t("cmsAggregatorTable.cid"),
        accessorFn: (row) => row.cid ?? "-",
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.aid"),
        accessorFn: (row) => row.aid ?? "-",
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.gameGroup"),
        accessorFn: (row) => row.group ?? "-",
        filterFn: "contains",
        size: 120,
      },
      {
        header: t("levelEnabled"),
        accessorFn: (row) => row.level.enabled ?? "-",
        filterFn: "contains",
      },
      {
        header: t("levelThreshold"),
        accessorFn: (row) => row.level.threshold ?? "-",
        filterFn: "contains",
      },
      {
        header: t("kickoutLowGoldEnabled"),
        accessorFn: (row) => row.kickout.low_gold.enabled ?? "-",
        filterFn: "contains",
      },
      {
        header: t("kickoutLowGoldThreshold"),
        accessorFn: (row) => row.kickout.low_gold.threshold ?? "-",
        filterFn: "contains",
      },
      {
        header: t("kickoutInactiveEnabled"),
        accessorFn: (row) => row.kickout.inactive.enabled ?? "-",
        filterFn: "contains",
      },
      {
        header: t("kickoutInactiveThreshold"),
        accessorFn: (row) => row.kickout.inactive.threshold ?? "-",
        filterFn: "contains",
      },
      {
        header: t("kickoutUnauthorizedEnabled"),
        accessorFn: (row) => row.kickout.unauthorized.enabled ?? "-",
        filterFn: "contains",
      },
      {
        header: t("kickoutUnauthorizedThreshold"),
        accessorFn: (row) => row.kickout.unauthorized.threshold ?? "-",
        filterFn: "contains",
      },
      {
        header: t("alcDistributeEnabled"),
        accessorFn: (row) => row.alc.distribute.enabled ?? "-",
        filterFn: "contains",
      },
      {
        header: t("alcDistributeThreshold"),
        accessorFn: (row) => row.alc.distribute.threshold ?? "-",
        filterFn: "contains",
      },
      {
        header: t("goldBuyPacks"),
        accessorFn: (row) => row.gold?.buy?.packs ?? "-",
        filterFn: "contains",
      },
      {
        header: t("goldBuyEnabled"),
        accessorFn: (row) => row.gold.buy?.enabled ?? "-",
        filterFn: "contains",
      },
      {
        header: t("goldBuyThreshold"),
        accessorFn: (row) => row.gold.buy?.threshold ?? "-",
        filterFn: "contains",
      },
      {
        header: t("goldDistributeEnabled"),
        accessorFn: (row) => row.gold.distribute?.enabled ?? "-",
        filterFn: "contains",
      },
      {
        header: t("goldDistributeThreshold"),
        accessorFn: (row) => row.gold.distribute?.threshold ?? "-",
        filterFn: "contains",
      },
      {
        header: t("gemsEnabled"),
        accessorFn: (row) => row.gems.enabled ?? "-",
        filterFn: "contains",
      },
      {
        header: t("gemsThreshold"),
        accessorFn: (row) => row.gems.threshold ?? "-",
        filterFn: "contains",
      },
      //from redis
      {
        header: t("pid"),
        accessorFn: (row) => row.club_data_from_redis.player_id ?? "-",
        filterFn: "contains",
      },
      {
        header: t("clubName"),
        accessorFn: (row) => row.club_data_from_redis.name ?? "-", //club name. Differ from config name
        filterFn: "contains",
      },
      {
        header: t("internalCid"),
        accessorFn: (row) => row.club_data_from_redis.internal_club_id ?? "-",
        filterFn: "contains",
      },
      {
        header: t("internalAllianceId"),
        accessorFn: (row) => row.club_data_from_redis.internal_alliance_id ?? "-",
        filterFn: "contains",
      },
      {
        header: t("alcBalance"),
        accessorFn: (row) => row.club_data_from_redis.alc_balance ?? "-",
        filterFn: "contains",
      },
      {
        header: t("isGold"),
        accessorFn: (row) => String(row.club_data_from_redis.is_gold ?? "-") ,
        filterFn: "contains",
      },
      {
        header: t("gold"),
        accessorFn: (row) => row.club_data_from_redis.gold ?? "-",
        filterFn: "contains",
      },
      {
        header: t("gems"),
        accessorFn: (row) => row.club_data_from_redis.gems ?? "-",
        filterFn: "contains",
      },
      {
        header: t("clubLevel"),
        accessorFn: (row) => row.club_data_from_redis.club_level ?? "-",
        filterFn: "contains",
      },
      {
        header: t("maxMembers"),
        accessorFn: (row) => row.club_data_from_redis.max_members ?? "-",
        filterFn: "contains",
      },
      {
        header: t("curMembers"),
        accessorFn: (row) => row.club_data_from_redis.cur_members ?? "-",
        filterFn: "contains",
      },
      {
        header: t("updatedAt"),
        accessorFn: (row) => getDate(row.club_data_from_redis.updated_at) ?? "-",
        filterFn: "contains"
      }
      /* player_id?: string;
          group_id?: string;
          club_id?: string;
          internal_club_id?: string;
          alliance_id?: string;
          internal_alliance_id?: string;
          gold?: number;
          gems?: number;
          alc_balance?: number;
          is_gold?: boolean;
          club_level?: number;
          max_members?: number;
          cur_members?: number;*/

      /*{
            header: t("cmsAggregatorTable.accountGems"),
            accessorFn: (row) => row?.cmsAggregatorRedisInfo?.account_gems ?? "-",
            filterFn: "contains",
          },
          {
            header: t("cmsAggregatorTable.clubGems"),
            accessorFn: (row) => row?.cmsAggregatorRedisInfo?.club_gems ?? "-",
            filterFn: "contains",
          },
          {
            header: t("cmsAggregatorTable.goldBalance"),
            accessorFn: (row) => row.cmsAggregatorRedisInfo?.gold_balance ?? "-",
            filterFn: "contains",
          },
          {
            header: t("cmsAggregatorTable.alcBalance"),
            accessorFn: (row) => row.cmsAggregatorRedisInfo?.alc_balance ?? "-",
            filterFn: "contains",
          },
          {
            header: t("cmsAggregatorTable.alcIssued"),
            accessorFn: (row) => row.cmsAggregatorRedisInfo?.alc_issued ?? "-",
            filterFn: "contains",
          },
          {
            header: t("cmsAggregatorTable.clubLevel"),
            accessorFn: (row) => row.cmsAggregatorRedisInfo?.club_level ?? "-",
            filterFn: "contains",
          },
          {
            header: t("cmsAggregatorTable.currMems"),
            accessorFn: (row) => row.cmsAggregatorRedisInfo?.cur_members ?? "-",
            filterFn: "contains",
          },
          {
            header: t("cmsAggregatorTable.maxMems"),
            accessorFn: (row) => row.cmsAggregatorRedisInfo?.max_members ?? "-",
            filterFn: "contains",
          },
          {
            header: t("cmsAggregatorTable.updatedAt"),
            accessorFn: (row) => row.cmsAggregatorRedisInfo?.updated_at ?? "-",
            Cell: ({row}) => {
            return moment
            .unix(Number(row.original.cmsAggregatorRedisInfo?.updated_at) / 1000)
            .utc(false)
            .format(DATE_FORMAT_WITH_UTC_TAG);
          },
            filterFn: "contains",
          },
          {
            header: t("cmsAggregatorTable.nlhExportTimestamp"),
            accessorFn: (row) => row.data?.nlh_export_timestamp ?? "-",
            Cell: ({row}) => {
            return moment
            .unix(Number(row.original.data?.nlh_export_timestamp) / 1000)
            .utc(false)
            .format(DATE_FORMAT_WITH_UTC_TAG);
          },
            filterFn: "contains",
          },
          {
            header: t("cmsAggregatorTable.ploExportTimestamp"),
            accessorFn: (row) => row.data?.plo_export_timestamp ?? "-",
            Cell: ({row}) => {
            return moment
            .unix(Number(row.original.data?.plo_export_timestamp) / 1000)
            .utc(false)
            .format(DATE_FORMAT_WITH_UTC_TAG);
          },
            filterFn: "contains",
          },
          {
            header: t("cmsAggregatorTable.nlhsExportTimestamp"),
            accessorFn: (row) => row.data?.nlhs_export_timestamp ?? "-",
            Cell: ({row}) => {
            return moment
            .unix(Number(row.original.data?.nlhs_export_timestamp) / 1000)
            .utc(false)
            .format(DATE_FORMAT_WITH_UTC_TAG);
          },
            filterFn: "contains",
          },
          {
            header: t("cmsAggregatorTable.ofcExportTimestamp"),
            accessorFn: (row) => row.data?.ofc_export_timestamp ?? "-",
            Cell: ({row}) => {
            return moment
            .unix(Number(row.original.data?.ofc_export_timestamp) / 1000)
            .utc(true)
            .format(DATE_FORMAT_WITH_UTC_TAG);
          },
            filterFn: "contains",
          },*/
    ],
    [t]
  );
};
